// Versioned migrations for the local snapshot.
//   v1 — baseline.
//   v2 — adds HSK mode (mode, customPersonas, hskLibrary, hskMastered, hskChatStates).
//   v3 — adds skipAuth (false) and savedAt (epoch) for the auth + sync layer.
//   v4 — adds aiProvider (defaults to deepseek) for the per-user model toggle.
//   v5 — adds favorites map ({}) for starred chats.
//   v6 — adds personaMemories map ({}) for periodic key-fact extraction.
//   v7 — drops Explore/Customize separation. Merges hskLibrary/hskMastered/
//        hskChatStates into the corresponding base fields and drops `mode`.
//        Custom personas now live in a single unified pool alongside curated.
//   v8 — viewedStories shifts from `Record<string, true>` to
//        `Record<string, string>` (the story-batch date). Legacy boolean
//        entries can't tell us which batch they applied to, so they're
//        dropped — worst case those rings appear "unwatched" once.
//   v9 — adds translationLanguage ('en' default). Picks the natural-
//        language translation the AI emits alongside each Chinese line.
//   v10 — drops translationLanguage. The Thai/English toggle was rolled
//         back; we revert to English-only AI translations and silently
//         strip the field from any persisted snapshot.
//   v11 — adds hasSeenWelcome (false) and skippedSetup (false). Back-
//         fills hasSeenWelcome=true for any snapshot that already has
//         profile.level set, so existing onboarded users don't re-see
//         the new two-option welcome screen.

import type { ChatState, LibraryWord } from '../types';
import type { AppSnapshot } from './repository';
import { EMPTY_SNAPSHOT, SCHEMA_VERSION } from './repository';

function migrateViewedStories(raw: unknown): Record<string, string> {
  if (!raw || typeof raw !== 'object') return {};
  const out: Record<string, string> = {};
  for (const [id, v] of Object.entries(raw as Record<string, unknown>)) {
    if (typeof v === 'string') out[id] = v;
  }
  return out;
}

function unionByHz(a: LibraryWord[] | undefined, b: LibraryWord[] | undefined): LibraryWord[] {
  const seen = new Set<string>();
  const out: LibraryWord[] = [];
  for (const w of [...(a ?? []), ...(b ?? [])]) {
    if (!w?.hz || seen.has(w.hz)) continue;
    seen.add(w.hz);
    out.push(w);
  }
  return out;
}

function mergeChatStates(
  a: Record<string, ChatState> | undefined,
  b: Record<string, ChatState> | undefined,
): Record<string, ChatState> {
  // Base side wins on key collision — chat history is per-persona and IDs are
  // unique across curated + custom personas (custom IDs are "custom-..."), so
  // in practice the two maps never share a key.
  return { ...(b ?? {}), ...(a ?? {}) };
}

export function migrate(raw: unknown): AppSnapshot {
  if (!raw || typeof raw !== 'object') return EMPTY_SNAPSHOT;
  const snap = raw as Partial<AppSnapshot> & {
    version?: number;
    mode?: string;
    hskLibrary?: LibraryWord[];
    hskMastered?: LibraryWord[];
    hskChatStates?: Record<string, ChatState>;
  };

  const profile = snap.profile ?? EMPTY_SNAPSHOT.profile;
  // v11 back-fill: any user with an existing level has already been through
  // the previous (pre-rewrite) onboarding flow, so treat them as if they've
  // seen the welcome screen.
  const inferredHasSeenWelcome = Boolean(profile.level);

  return {
    version: SCHEMA_VERSION,
    profile,
    aiProvider: snap.aiProvider === 'anthropic' ? 'anthropic' : 'deepseek',
    skipAuth: typeof snap.skipAuth === 'boolean' ? snap.skipAuth : false,
    nudgeDismissed: typeof snap.nudgeDismissed === 'boolean' ? snap.nudgeDismissed : false,
    hasSeenWelcome:
      typeof snap.hasSeenWelcome === 'boolean' ? snap.hasSeenWelcome : inferredHasSeenWelcome,
    skippedSetup: typeof snap.skippedSetup === 'boolean' ? snap.skippedSetup : false,
    savedAt: typeof snap.savedAt === 'string' ? snap.savedAt : EMPTY_SNAPSHOT.savedAt,
    library: unionByHz(snap.library, snap.hskLibrary),
    mastered: unionByHz(snap.mastered, snap.hskMastered),
    relationships: snap.relationships ?? {},
    chatStates: mergeChatStates(snap.chatStates, snap.hskChatStates),
    viewedStories: migrateViewedStories(snap.viewedStories),
    favorites: snap.favorites ?? {},
    personaMemories: snap.personaMemories ?? {},
    customPersonas: Array.isArray(snap.customPersonas) ? snap.customPersonas : [],
  };
}
